import React, { useCallback, useReducer, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import Section from "./Section";
import Container from "./Container";
import SafeSiteImage from "./SafeSiteImage";
import randomstring from "randomstring";
import { FormField } from "./Form";
import CloseV2 from "./Icons/CloseV2";

const SubscribeToNewsletterStyles = styled.div`
  ${({ ...props }) => css`
    section {
      padding: 24px 0;
    }

    .subscribe-to-newsletter-container {
      display: flex;
      flex-flow: column;
      row-gap: 2rem;
      column-gap: clamp(2rem, 4vw, 6rem);

      .icon-content-wrapper {
        display: flex;
        align-items: center;
        flex-flow: column;
        text-align: center;
        gap: 1rem;
      }

      h2 {
        font-size: 2rem
      }

      .icon {
        width: 100px;
        height: 100px;
      }

      p {
        margin-top: 1rem;
        font-size: 1rem
      }

      .button {
        background-color: ${props.theme.colors.darkGrey};
        text-align: center;
        font-size: 1.2rem;
        display: block;
        min-width: 350px;
        margin-left: auto;
        margin-right: auto;

        &:hover {
          background-color: ${props.theme.colors.darkGreyHover}
        }
      }

      @media (min-width: ${props.theme.breakpoints.md}) {
        flex-flow: row;
        align-items: center;
        justify-content: center;

        .icon-content-wrapper {
          flex-flow: row;
          gap: 2rem;
          text-align: left;

          p {
            margin-top: 0;
          }
        }

        .button {
          margin-left: 0;
          margin-right: 0;
        }
      }
    }
  `}
`;

// ToDo
//  Abstract out this styling with the QuoteBasket modal
const StyledSubscribeToNewsletterModal = styled.div`
  ${({ ...props }) => css`
    background-color: rgb(8 8 8 / 84%);
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 999999999999999;
    overflow: auto;

    @media (min-width: ${props.theme.breakpoints.md}) {
      z-index: 100;
    }

    > div {
      background-color: #fff;
      overflow: hidden;
      border-radius: 4px;
      max-width: 960px;
      margin: 2rem auto;
    }

    header {
      background-color: ${props.theme.colors.primary};
      color: #fff;
      padding: 24px;
      position: relative;
      padding-right: 70px;

      p {
        font-size: 1rem
      }
    }

    img {
      width: 100%;
      height: 250px;
      object-fit: cover;
      object-position: center;
    }
    
    section {
      padding: 24px;
      background-color: #ECECE9;

      form {
        display: grid;
        gap: 16px;

        div {
          margin-bottom: 0;
        }

        p {
          margin-top: 1rem;
          grid-row: 5;
          font-size: 1rem;
          text-align: center;
        }
      }

      .response_message {
        font-size: 2rem;
        line-height: 1.33;
        text-align: center;
        max-width: 500px;
        margin: auto;
        color: #7F7F7F;
        font-weight: bold;
        padding-top: 2rem;
        padding-bottom: 2rem;

        &.error {
          color: red
        }
      }
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      section {
        padding: 48px;

        form {
          gap: 24px;
          grid-template-columns: repeat(4, 1fr);

          div {
            grid-column: span 2;
          }

          div:has(input[type="email"]) {
            grid-column: span 4;
          }

          p {
            margin-top: 0;
            grid-row: auto;
            grid-column: span 3;
            text-wrap: balance;
            text-align: left;
          }
        }
      }
    }
  `}
`;

const StyledCloseButton = styled.button`
  ${({ ...props }) => css`
    position: absolute;
    right: 24px;
    top: 24px;
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 767px) {
      padding-right: 0;
      span {
        display: none;
      }
    }
  `}
`;

const SubmitButtonStyles = styled.button`
  ${({ ...props }) => css`
    background-color: ${props.theme.colors.darkGrey};
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15), 0 2px 2px rgba(0, 0, 0, 0.15),
      0 4px 4px rgba(0, 0, 0, 0.15), 0 8px 8px rgba(0, 0, 0, 0.15);
    min-width: 200px;
    cursor: pointer;

    &:hover {
      background-color: ${props.theme.colors.darkGreyHover};
    }

    &:disabled {
      cursor: not-allowed;
      pointer-events: none;
      background-color: ${props.theme.colors.darkGreyHover};
    }
  `}
`;

const ModalInner = React.memo(({
  disclaimer,
  confirmation
}) => {
  const rand = useMemo(() => randomstring.generate(5), []);

  const [serverState, setServerState] = useState({
    submitting: false,
    success: null,
  });

  const initialFormData = {
    firstName: "",
    lastName: "",
    email: "",
  }

  function formReducer(state, action) {
    switch (action.type) {
      case "CHANGE":
        return { ...state, [action.field]: action.value };
      case "RESET":
        return initialFormData;
      default:
        throw new Error("Unknown action type");
    }
  }

  const [formData, dispatch] = useReducer(formReducer, initialFormData);

  const handleFormChange = (e) => {
    dispatch({ type: "CHANGE", field: e.target.name, value: e.target.value });
  };

  const handleOnSubmit = useCallback((e) => {
    e.preventDefault();
    setServerState({ submitting: true, success: null });

    const { firstName, lastName, email } = formData;

    fetch(
      '/.netlify/functions/subscribe',
      {
        method: 'POST',
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.toLowerCase(),
        })
      }
    )
      .then((r) => {
        if (!r.ok) {
          throw new Error(`Request failed with status ${r.status}`);
        }

        setServerState({ submitting: false, success: true });
        dispatch({ type: "RESET" });
      })
      .catch((e) => {
        console.error(e)

        if (process.env.STORYBOOK) {
          setTimeout(() => {
            setServerState({ submitting: false, success: true });
          }, 1000);
        } else {
          setServerState({ submitting: false, success: false });
        }
      });
  }, [formData]);

  if (serverState.submitting) {
    return (
      <p className="response_message">Subscribing...</p>
    );
  }

  if (serverState.success === true) {
    return (
      <p className="response_message success">{confirmation}</p>
    );
  }

  if (serverState.success === false) {
    return (
      <p className="response_message error">There was an error subscribing you to our mailing list. Please try again later.</p>
    );
  }

  return (
    <form onSubmit={handleOnSubmit} onChange={handleFormChange}>
      <FormField>
        <input
          type="text"
          name="firstName"
          id={`${rand}_first_name`}
          required
          placeholder="First Name"
          value={formData.firstName}
          onChange={handleFormChange}
        />
        <label htmlFor={`${rand}_first_name`}>First Name</label>
      </FormField>
      <FormField>
        <input
          type="text"
          name="lastName"
          id={`${rand}_last_name`}
          required
          placeholder="Last Name"
          value={formData.lastName}
          onChange={handleFormChange}
        />
        <label htmlFor={`${rand}_last_name`}>Last Name</label>
      </FormField>
      <FormField>
        <input
          type="email"
          name="email"
          id={`${rand}_email`}
          required
          placeholder="Email"
          value={formData.email}
          onChange={handleFormChange}
        />
        <label htmlFor={`${rand}_email`}>Email</label>
      </FormField>
      <p>{disclaimer}</p>
      <SubmitButtonStyles
        type="submit"
        disabled={serverState.submitting || !formData.firstName || !formData.lastName || !formData.email}
      >
        Subscribe
      </SubmitButtonStyles>
    </form>
  );
});

const SubscribeToNewsletterModal = ({
  active,
  setActive,
  title,
  subText,
  disclaimer,
  confirmation,
  image
}) => {
  const rand = useMemo(() => randomstring.generate(5), []);

  const handleBackgroundClick = useCallback((e) => {
    if (e.target.id === `${rand}_subscribe-to-newsletter-background`) {
      setActive(false);
    }
  }, [rand, setActive]);

  return (
    <>
      {active && (
        <StyledSubscribeToNewsletterModal
          onClick={handleBackgroundClick}
          id={`${rand}_subscribe-to-newsletter-background`}
        >
          <div>
            <header>
              <h2>{title}</h2>
              <p>{subText}</p>
              <StyledCloseButton onClick={() => setActive(false)}>
                <CloseV2 />
                <span>Close</span>
              </StyledCloseButton>
            </header>
            <SafeSiteImage image={image.mediaItemUrl} loading={'eager'} fetchpriority={'high'} width={960} height={250} fit={'cover'} />
            <section>
              <ModalInner disclaimer={disclaimer} confirmation={confirmation} />
            </section>
          </div>
        </StyledSubscribeToNewsletterModal>
      )}
    </>
  );
};

export const SubscribeToNewsletter = ({
  background = 'white',
  title = '',
  subText = '',
  disclaimer = '',
  confirmation = '',
  icon,
  image
}) => {
  const [subscribeModalIsActive, setSubscribeModalIsActive] = useState(false);

  return (
    <SubscribeToNewsletterStyles>
      <SubscribeToNewsletterModal
        active={subscribeModalIsActive}
        setActive={setSubscribeModalIsActive}
        title={title}
        subText={subText}
        disclaimer={disclaimer}
        confirmation={confirmation}
        image={image}
      />
      <Section
        grey={background === 'grey'}
      >
        <Container>
          <div className="subscribe-to-newsletter-container">
            <div className="icon-content-wrapper">
              <SafeSiteImage image={icon.mediaItemUrl} width={100} height={100} className="icon" />
              <div className="text">
                <h2>{title}</h2>
                <p>{subText}</p>
              </div>
            </div>
            <a href="javascript:void(0)" className="button" onClick={() => setSubscribeModalIsActive(true)}>
              Subscribe
            </a>
          </div>
        </Container>
      </Section>
    </SubscribeToNewsletterStyles>
  );
};
